/**
 * @biomax/plugin-region-database — versioned region catalog for the
 * BioMax epidemiology engine.
 *
 * v0.2 ships country-level records for ~12 priority jurisdictions
 * (US, GB, IND, AUS, NZL, CHN, BRA, ZAF, NGA, JPN, DEU, FRA).
 * v0.3+ expands to all 195 UN member states + subdivision-level for
 * top-50.
 *
 * Pure data layer. Browser-safe entry; Node fs loader in
 * src/loader.ts. Audit gate enforces internal consistency
 * (population × density × area; urban fraction; healthcare-capacity
 * bounds).
 */

import type { PluginManifest } from '@biomax/types';

export * from './types.js';
export { validateRegion, type ValidationResult } from './validate.js';
export {
  reload, getLoadFailures, __setDataDirectory, __setRegionRecords,
} from './catalog.js';
export { auditRegion, type AuditFinding, type AuditSeverity } from './audit.js';

import { loaderFindRegion, loaderListRegions } from './catalog.js';
import type { Region } from './types.js';

const PLUGIN_ID = 'region-database';

export function findRegion(idOrIso3: string): Region | undefined {
  return loaderFindRegion(idOrIso3);
}

export function listRegions(): Region[] {
  return loaderListRegions();
}

export class RegionNotFoundError extends Error {
  constructor(idOrIso3: string) {
    super(`Region not found: "${idOrIso3}"`);
    this.name = 'RegionNotFoundError';
  }
}

export function requireRegion(idOrIso3: string): Region {
  const r = findRegion(idOrIso3);
  if (!r) throw new RegionNotFoundError(idOrIso3);
  return r;
}

export const manifest: PluginManifest = {
  id: PLUGIN_ID,
  version: '0.1.0',
  engineCompat: '^0.5.0',
  kingdoms: '*',
  systems: [],
  components: [],
};

// Phase 9.1 — Cultural / Political Contextual Risk Layer schema + loader +
// query API + audit gate. Complements (does NOT replace) the per-region
// CulturalSpec — adds the rich (region × pathogen-class × domain) join with
// citation-enforcement, evidence-strength, sensitivity flags, and anti-
// stereotyping audit rails. Seed entries land in Phase 9.2.
export {
  loadCulturalRiskRegistry, loadPendingCulturalRiskReview,
  queryCulturalRiskFactors, findCulturalRiskFactor,
  dataConfidenceFor, auditCulturalRiskRegistry,
  __setCulturalRiskRegistry, __resetCulturalRiskRegistry,
  type CulturalRiskFactor, type CulturalRiskQuery, type CulturalRiskAuditFinding,
  type CulturalRiskCitation, type CulturalRiskExampleCase, type CulturalRiskOperatorAction,
  type PathogenClass, type CulturalRiskDomain, type EvidenceStrength,
  type Temporality, type SensitivityFlag, type RecommendedMessenger,
  type OperatorSurfaceContext,
} from './cultural-risk.js';
